import { isCanceledRequest } from 'Shared/network/canceledRequest';
import { AxiosError } from 'axios';
import { AuthorizationError, TOKEN_NOT_EXIST, UnknownError } from 'viewup-lib/common';
import { maskIdSegment } from './maskIdSegment';
import { reportHandledWarning, type WarnContext } from './reportHandledWarning';

/**
 * 요청에 실려 나간 `x-requester-id`(= 로그인 사용자의 user uuid)를 읽는다.
 *
 * `HeaderInterceptor`가 토큰에서 `getUserUuidFromToken`으로 뽑아 넣는 값이라, 이걸 그대로
 * 리포트에 실으면 **백엔드 로그·트레이스와 같은 키로 사용자를 대조**할 수 있다.
 * 비로그인이면 interceptor가 `undefined`를 넣고 axios가 헤더를 드롭하므로 값이 없다.
 *
 * bracket 대입(`HeaderInterceptor`의 방식)과 `AxiosHeaders.set()` 양쪽을 모두 커버한다 —
 * 저장 위치가 달라 한쪽만 읽으면 조용히 빗나간다.
 */
function getRequesterId(error: AxiosError): string | undefined {
  const headers = error.config?.headers as
    | (Record<string, unknown> & { get?: (name: string) => unknown })
    | undefined;
  const value = headers?.['x-requester-id'] ?? headers?.get?.('x-requester-id');

  return typeof value === 'string' && value !== '' ? value : undefined;
}

/**
 * path 의 리소스 id 세그먼트를 placeholder 로 치환한다.
 *
 * 정규화하지 않으면 **같은 엔드포인트의 실패가 리소스 id 별로 흩어져** SigNoz 에서
 * "어느 API 가 얼마나 실패하나"를 집계할 수 없다. 실패 1건을 볼 때는 id 가 있는 게 낫지만,
 * 이 리포트의 용도는 집계이고 개별 요청은 trace 로 따라간다.
 */
function normalizeApiPath(path: string | undefined): string | undefined {
  if (path === undefined) return undefined;

  return path.split('/').map(maskIdSegment).join('/');
}

export function reportApiWarning(scope: string, error: unknown, context?: WarnContext): void {
  // 취소된 요청 — 언마운트·네비게이션·큐 정리로 끊은 정상 흐름이다. 서버에 도달하지도 않은
  // 요청이라 실패로 셀 근거가 없고, 화면 전환이 잦은 뷰잉 목록에서 다량으로 발생해
  // rolling 리포트 예산을 잠식한다. 승격 차단은 `errorInterceptor` 가 하고, 이 제외는
  // 그 뒤에도 react-query onError 로 흘러드는 원본 취소 에러를 막는다 — 같은 판정 함수를 쓴다.
  if (isCanceledRequest(error)) {
    return;
  }

  // 429 tooManyRequests — RateLimitOverlay가 처리하는 정상 rate-limit UX 흐름.
  // 웹이 이미 errorInterceptor / queryCache / mutationCache 3곳에서 명시 제외한다.
  if (
    error instanceof AxiosError &&
    error.response?.status === 429 &&
    error.response.data?.code === 'tooManyRequests'
  ) {
    return;
  }

  // 토큰 만료 → 홈 리다이렉트는 정상 로그아웃 흐름. (네이티브도 refresh의 !res.ok를 제외)
  if (error instanceof AuthorizationError && error.message === TOKEN_NOT_EXIST) {
    return;
  }

  // errorInterceptor가 "백엔드가 code:'unknown'으로 응답"한 경우 UnknownError로 감싸면서
  // axios response가 소실된다 → 보정하지 않으면 백엔드 에러가 origin=frontend로 오태깅된다.
  // NetworkError는 보정하지 않는다 — 전송 실패이므로 기본 분류(frontend)가 맞다.
  const originOverride: WarnContext = error instanceof UnknownError ? { origin: 'backend' } : {};

  // 요청 식별자 자동 추출 — 앱에 mutationKey를 설정하는 mutation이 없어 mutation 리포트가
  // 엔드포인트 식별 불가였다. url은 쿼리스트링에 PII(예: ?id=<로그인 아이디>)가 실릴 수 있어 path만
  // 남기고, 그 path의 리소스 id는 집계 가능하도록 정규화한다(`normalizeApiPath`).
  // userUuid는 로그인 상태에서만 붙는다(x-requester-id 헤더) — 비로그인이면 키 자체가 없다.
  const request: WarnContext =
    error instanceof AxiosError
      ? {
          method: error.config?.method?.toUpperCase(),
          url: normalizeApiPath(error.config?.url?.split('?')[0]),
          userUuid: getRequesterId(error),
        }
      : {};

  reportHandledWarning(scope, error, { ...originOverride, ...request, ...context });
}
